import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, AlertTriangle, Send } from "lucide-react";
import { publicApi, errMsg } from "@/lib/api";

// ----------------------------------------------------------------------
// GFMP Sync Catalogue Inquiry · /gfmp/sync-catalogue/inquiry
// Public, unauthenticated. Posts to /api/public/gfmp/inquiry. Server
// creates a Sync Opportunity record with status "New Inquiry". No
// license, quote, or rights confirmation is ever promised.
// ----------------------------------------------------------------------

const INK     = "hsl(20 14% 6%)";
const INK_2   = "hsl(20 12% 9%)";
const CREAM   = "hsl(36 33% 96%)";
const CREAM_2 = "hsl(36 22% 93%)";
const BURG    = "hsl(347 60% 22%)";
const GOLD    = "hsl(40 48% 58%)";
const CHAR    = "hsl(20 10% 16%)";

const FIELD_INPUT_STYLE = {
  background: INK_2,
  color: CREAM,
  border: `1px solid ${GOLD}33`,
};

const BLANK_FORM = {
  contact_name: "",
  company: "",
  email: "",
  phone: "",
  project_title: "",
  project_type: "",
  intended_usage: "",
  territory: "",
  requested_term: "",
  budget_range: "",
  deadline: "",
  tracks_of_interest: [],
  additional_notes: "",
};

const PROJECT_TYPE_FALLBACK = [
  "Film",
  "Television",
  "Advertising",
  "Trailer",
  "Game",
  "Digital content",
  "Live event",
  "Other",
];

function Label({ htmlFor, required, children }) {
  return (
    <label
      htmlFor={htmlFor}
      className="block text-[11px] uppercase tracking-[0.18em] mb-1.5"
      style={{ color: `${GOLD}cc` }}
    >
      {children}
      {required && <span style={{ color: `${BURG}cc` }} className="ml-1">*</span>}
    </label>
  );
}

function Field({ id, type = "text", value, onChange, required, placeholder, autoComplete, dataTestId, maxLength }) {
  return (
    <input
      id={id}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      autoComplete={autoComplete}
      maxLength={maxLength}
      data-testid={dataTestId}
      className="w-full rounded-md px-3 py-2 text-[13.5px] outline-none focus:ring-1"
      style={{ ...FIELD_INPUT_STYLE, boxShadow: "inset 0 0 0 1px transparent" }}
    />
  );
}

function TextArea({ id, value, onChange, rows = 4, placeholder, dataTestId, maxLength }) {
  return (
    <textarea
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      maxLength={maxLength}
      data-testid={dataTestId}
      className="w-full rounded-md px-3 py-2 text-[13.5px] outline-none focus:ring-1"
      style={{ ...FIELD_INPUT_STYLE }}
    />
  );
}

function SelectField({ id, value, onChange, options, dataTestId }) {
  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={dataTestId}
      className="w-full rounded-md px-3 py-2 text-[13.5px] outline-none focus:ring-1"
      style={{ ...FIELD_INPUT_STYLE }}
    >
      <option value="">Select…</option>
      {options.map((o) => (
        <option key={o} value={o} style={{ color: "#111" }}>
          {o}
        </option>
      ))}
    </select>
  );
}

function NoticeBlock() {
  return (
    <div
      className="rounded-md px-4 py-3 text-[12.5px] leading-relaxed"
      data-testid="gfmp-inquiry-notice"
      style={{
        background: `${BURG}22`,
        border: `1px solid ${BURG}66`,
        color: `${CREAM}e0`,
      }}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: GOLD }} aria-hidden="true" />
        <p>
          Submission of an inquiry does not grant any license, usage right, quote, clearance
          confirmation, or permission to use any music. All requests remain subject to rights review,
          availability, written approval, and a fully executed license agreement.
        </p>
      </div>
    </div>
  );
}

export default function GFMPSyncInquiry() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(BLANK_FORM);
  const [projectTypes, setProjectTypes] = useState(PROJECT_TYPE_FALLBACK);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [error, setError] = useState("");
  const [prefilledTitle, setPrefilledTitle] = useState("");

  function setField(k, v) {
    setForm((s) => ({ ...s, [k]: v }));
  }

  // Pre-fill from query params if arriving from a card "Request Information".
  useEffect(() => {
    const trackId = params.get("track") || "";
    const title = params.get("title") || "";
    if (trackId) {
      setForm((s) => ({
        ...s,
        tracks_of_interest: Array.from(new Set([...(s.tracks_of_interest || []), trackId])),
      }));
      setPrefilledTitle(title);
    }
  }, [params]);

  // Pull dynamic project type list (graceful fallback if endpoint unavailable).
  useEffect(() => {
    let cancelled = false;
    publicApi
      .get("/public/gfmp/meta")
      .then((r) => {
        if (cancelled) return;
        const list = Array.isArray(r.data?.inquiry_project_types)
          ? r.data.inquiry_project_types
          : PROJECT_TYPE_FALLBACK;
        setProjectTypes(list);
      })
      .catch(() => {
        if (!cancelled) setProjectTypes(PROJECT_TYPE_FALLBACK);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (!form.contact_name.trim()) {
      setError("Please tell us your name.");
      return;
    }
    if (!form.email.trim()) {
      setError("Please provide an email address.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await publicApi.post("/public/gfmp/inquiry", form);
      setSubmitted(r.data);
      // Reset to a clean state so a user double-tap cannot accidentally resend.
      setForm(BLANK_FORM);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(errMsg(e, "Unable to submit your inquiry. Please try again or email the Sync Desk."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      data-testid="gfmp-sync-inquiry-page"
      className="min-h-screen w-full flex flex-col"
      style={{ background: INK, color: CREAM }}
    >
      {/* Top nav */}
      <header
        className="w-full"
        style={{ borderBottom: `1px solid ${GOLD}26`, background: INK }}
      >
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-5 flex items-center justify-between gap-4">
          <Link
            to="/gfmp/sync-catalogue"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] transition-opacity hover:opacity-80"
            style={{ color: `${CREAM}cc` }}
            data-testid="gfmp-inquiry-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Sync Catalogue
          </Link>
          <span
            className="text-[10px] uppercase tracking-[0.22em] hidden sm:inline-block"
            style={{ color: `${GOLD}aa` }}
          >
            Licensing Inquiry
          </span>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
          <div
            className="text-[10px] uppercase tracking-[0.24em] mb-3"
            style={{ color: GOLD }}
            data-testid="gfmp-inquiry-eyebrow"
          >
            GFMP Sync Desk
          </div>
          <h1
            className="font-display text-[30px] sm:text-[40px] leading-[1.05] tracking-tight"
            data-testid="gfmp-inquiry-heading"
            style={{ color: CREAM }}
          >
            Request{" "}
            <span style={{ color: GOLD }}>Licensing Information</span>
          </h1>
          <p
            className="mt-4 text-[14px] leading-relaxed max-w-2xl"
            style={{ color: `${CREAM}b8` }}
          >
            Use this form to share a brief with the GFMP Sync Desk. Our team will follow up
            directly. We do not auto-confirm availability, rights status, or license terms.
          </p>

          {submitted ? (
            <div
              className="mt-10 rounded-2xl px-6 sm:px-8 py-7"
              data-testid="gfmp-inquiry-success"
              style={{ background: INK_2, border: `1px solid ${GOLD}44` }}
            >
              <CheckCircle2 className="w-7 h-7 mb-3" style={{ color: GOLD }} />
              <div
                className="text-[10px] uppercase tracking-[0.22em] mb-1"
                style={{ color: `${GOLD}cc` }}
              >
                Received
              </div>
              <h2 className="font-display text-[22px] leading-tight" style={{ color: CREAM }}>
                Your inquiry has been logged with the GFMP Sync Desk.
              </h2>
              <p
                className="mt-4 text-[13px] leading-relaxed"
                style={{ color: `${CREAM}cc` }}
                data-testid="gfmp-inquiry-success-notice"
              >
                {submitted.notice}
              </p>
              <div
                className="mt-6 text-[11px] uppercase tracking-[0.18em]"
                style={{ color: `${CREAM}99` }}
              >
                Reference:{" "}
                <span className="font-mono normal-case" data-testid="gfmp-inquiry-reference">
                  {submitted.id?.slice(0, 8)}
                </span>
              </div>
              <div className="mt-7 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => navigate("/gfmp/sync-catalogue")}
                  className="text-[11px] uppercase tracking-[0.22em] px-4 py-2 rounded-full transition-opacity hover:opacity-90"
                  style={{ background: GOLD, color: INK }}
                  data-testid="gfmp-inquiry-success-back"
                >
                  Back to Sync Catalogue
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSubmitted(null);
                    setForm(BLANK_FORM);
                  }}
                  className="text-[11px] uppercase tracking-[0.22em] px-4 py-2 rounded-full"
                  style={{ background: "transparent", color: CREAM, border: `1px solid ${GOLD}55` }}
                  data-testid="gfmp-inquiry-success-new"
                >
                  Submit Another
                </button>
              </div>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="mt-8 space-y-7"
              data-testid="gfmp-inquiry-form"
              noValidate
            >
              <NoticeBlock />

              {error && (
                <div
                  role="alert"
                  className="rounded-md px-4 py-3 text-[12.5px]"
                  style={{ background: `${BURG}33`, border: `1px solid ${BURG}88`, color: CREAM }}
                  data-testid="gfmp-inquiry-error"
                >
                  {error}
                </div>
              )}

              {/* Contact */}
              <section
                className="space-y-4 rounded-xl p-5"
                style={{ background: INK_2, border: `1px solid ${GOLD}22` }}
              >
                <div
                  className="text-[10px] uppercase tracking-[0.24em]"
                  style={{ color: GOLD }}
                >
                  Contact
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact_name" required>Name</Label>
                    <Field
                      id="contact_name"
                      value={form.contact_name}
                      onChange={(v) => setField("contact_name", v)}
                      required
                      maxLength={160}
                      autoComplete="name"
                      dataTestId="gfmp-inquiry-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="company">Company / Organization</Label>
                    <Field
                      id="company"
                      value={form.company}
                      onChange={(v) => setField("company", v)}
                      maxLength={200}
                      autoComplete="organization"
                      dataTestId="gfmp-inquiry-company"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" required>Email</Label>
                    <Field
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(v) => setField("email", v)}
                      required
                      maxLength={200}
                      autoComplete="email"
                      dataTestId="gfmp-inquiry-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone (optional)</Label>
                    <Field
                      id="phone"
                      type="tel"
                      value={form.phone}
                      onChange={(v) => setField("phone", v)}
                      maxLength={60}
                      autoComplete="tel"
                      dataTestId="gfmp-inquiry-phone"
                    />
                  </div>
                </div>
              </section>

              {/* Project */}
              <section
                className="space-y-4 rounded-xl p-5"
                style={{ background: INK_2, border: `1px solid ${GOLD}22` }}
              >
                <div
                  className="text-[10px] uppercase tracking-[0.24em]"
                  style={{ color: GOLD }}
                >
                  Project
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="project_title">Project Title</Label>
                    <Field
                      id="project_title"
                      value={form.project_title}
                      onChange={(v) => setField("project_title", v)}
                      maxLength={200}
                      dataTestId="gfmp-inquiry-project-title"
                    />
                  </div>
                  <div>
                    <Label htmlFor="project_type">Project Type</Label>
                    <SelectField
                      id="project_type"
                      value={form.project_type}
                      onChange={(v) => setField("project_type", v)}
                      options={projectTypes}
                      dataTestId="gfmp-inquiry-project-type"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="intended_usage">Intended Usage</Label>
                    <TextArea
                      id="intended_usage"
                      value={form.intended_usage}
                      onChange={(v) => setField("intended_usage", v)}
                      placeholder="Describe the placement, scene, or campaign context."
                      rows={3}
                      maxLength={1500}
                      dataTestId="gfmp-inquiry-usage"
                    />
                  </div>
                  <div>
                    <Label htmlFor="territory">Territory</Label>
                    <Field
                      id="territory"
                      value={form.territory}
                      onChange={(v) => setField("territory", v)}
                      placeholder="e.g. United States, Worldwide"
                      maxLength={200}
                      dataTestId="gfmp-inquiry-territory"
                    />
                  </div>
                  <div>
                    <Label htmlFor="requested_term">Requested Term</Label>
                    <Field
                      id="requested_term"
                      value={form.requested_term}
                      onChange={(v) => setField("requested_term", v)}
                      placeholder="e.g. In-perpetuity, 5 years, broadcast window"
                      maxLength={200}
                      dataTestId="gfmp-inquiry-term"
                    />
                  </div>
                  <div>
                    <Label htmlFor="budget_range">Budget Range (optional)</Label>
                    <Field
                      id="budget_range"
                      value={form.budget_range}
                      onChange={(v) => setField("budget_range", v)}
                      placeholder="Optional"
                      maxLength={120}
                      dataTestId="gfmp-inquiry-budget"
                    />
                  </div>
                  <div>
                    <Label htmlFor="deadline">Deadline</Label>
                    <Field
                      id="deadline"
                      value={form.deadline}
                      onChange={(v) => setField("deadline", v)}
                      placeholder="e.g. 2026-12-31 or 'flexible'"
                      maxLength={120}
                      dataTestId="gfmp-inquiry-deadline"
                    />
                  </div>
                </div>
              </section>

              {/* Tracks of interest */}
              <section
                className="space-y-3 rounded-xl p-5"
                style={{ background: INK_2, border: `1px solid ${GOLD}22` }}
              >
                <div
                  className="text-[10px] uppercase tracking-[0.24em]"
                  style={{ color: GOLD }}
                >
                  Tracks of Interest (if applicable)
                </div>
                {form.tracks_of_interest.length > 0 ? (
                  <ul className="flex flex-wrap gap-2" data-testid="gfmp-inquiry-track-chips">
                    {form.tracks_of_interest.map((id) => (
                      <li
                        key={id}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px]"
                        style={{ background: `${GOLD}1a`, color: CREAM, border: `1px solid ${GOLD}55` }}
                      >
                        {prefilledTitle ? prefilledTitle : "Selected from catalogue"}
                        <button
                          type="button"
                          onClick={() =>
                            setField(
                              "tracks_of_interest",
                              form.tracks_of_interest.filter((t) => t !== id)
                            )
                          }
                          className="opacity-70 hover:opacity-100"
                          aria-label="Remove selection"
                          data-testid="gfmp-inquiry-track-remove"
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-[12px]" style={{ color: `${CREAM}99` }}>
                    No specific track selected. You can describe what you’re looking for in the
                    Additional Notes field below.
                  </p>
                )}
              </section>

              {/* Notes */}
              <section
                className="space-y-3 rounded-xl p-5"
                style={{ background: INK_2, border: `1px solid ${GOLD}22` }}
              >
                <div
                  className="text-[10px] uppercase tracking-[0.24em]"
                  style={{ color: GOLD }}
                >
                  Additional Notes
                </div>
                <TextArea
                  id="additional_notes"
                  value={form.additional_notes}
                  onChange={(v) => setField("additional_notes", v)}
                  rows={5}
                  placeholder="Anything else the Sync Desk should know — mood references, tempo, similar tracks, brief PDF coming separately, etc."
                  maxLength={2000}
                  dataTestId="gfmp-inquiry-notes"
                />
              </section>

              <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
                <p
                  className="text-[11.5px] max-w-xl leading-relaxed"
                  style={{ color: `${CREAM}99` }}
                >
                  By submitting this form, you confirm that you understand and accept the
                  non-license notice above.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] px-5 py-2.5 rounded-full transition-opacity hover:opacity-90 disabled:opacity-50"
                  style={{ background: GOLD, color: INK }}
                  data-testid="gfmp-inquiry-submit"
                >
                  <Send className="w-3.5 h-3.5" />
                  {submitting ? "Submitting…" : "Submit Inquiry"}
                </button>
              </div>
            </form>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer
        className="w-full"
        style={{ background: INK_2, color: `${CREAM}99`, borderTop: `1px solid ${GOLD}26` }}
        data-testid="gfmp-inquiry-footer"
      >
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-7 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-col">
            <span className="font-display text-[16px]" style={{ color: CREAM }}>
              Good Friar Music Publishing
            </span>
            <span className="text-[10px] uppercase tracking-[0.22em]" style={{ color: `${GOLD}aa` }}>
              A Durban 49 Group Company
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/gfmp/sync-catalogue"
              className="text-[11px] uppercase tracking-[0.22em] hover:opacity-80"
              style={{ color: `${CREAM}cc` }}
            >
              Sync Catalogue
            </Link>
            <Link
              to="/gfmp"
              className="text-[11px] uppercase tracking-[0.22em] hover:opacity-80"
              style={{ color: `${CREAM}cc` }}
            >
              GFMP
            </Link>
          </div>
        </div>
        <div
          className="max-w-6xl mx-auto px-6 sm:px-10 pb-6 text-[11px]"
          style={{ color: `${CREAM_2}66` }}
        >
          © {new Date().getFullYear()} Good Friar Music Publishing. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
